/**
 * MessageCompose Component
 * Input area for sending messages to AI
 */

import React, { useState, useRef, KeyboardEvent } from 'react';
import {
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Send as SendIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const ComposeContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'flex-end',
  gap: theme.spacing(1),
  backgroundColor: theme.palette.background.paper
}));

interface MessageComposeProps {
  onSend: (content: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const MessageCompose: React.FC<MessageComposeProps> = ({
  onSend,
  disabled = false,
  placeholder = 'Type your message...'
}) => {
  const [message, setMessage] = useState('');
  const textFieldRef = useRef<HTMLInputElement>(null);

  const handleSend = () => {
    const trimmedMessage = message.trim();
    if (trimmedMessage && !disabled) {
      onSend(trimmedMessage);
      setMessage('');
      // Focus back on the input after sending
      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 0);
    }
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    // Send on Enter, but allow Shift+Enter for new lines
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <ComposeContainer data-testid="message-compose">
      <TextField
        fullWidth
        multiline
        maxRows={6}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        inputRef={textFieldRef}
        data-testid="message-input"
        variant="outlined"
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            paddingRight: 0
          }
        }}
      />
      <Tooltip title={disabled ? 'Sending...' : 'Send message (Enter)'}>
        <span>
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={disabled || !message.trim()}
            data-testid="send-button"
            sx={{
              mb: 0.5
            }}
          >
            {disabled ? <CircularProgress size={24} /> : <SendIcon />}
          </IconButton>
        </span>
      </Tooltip>
    </ComposeContainer>
  );
};

export default MessageCompose;
